import { Box, Typography } from '@mui/material';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import Layout from '../../components/core/layout';
import Loading from '../../components/core/loading';
import SessionContext from '../../contexts/session';
import { refreshUserAndStoreIn } from '../../logic/auth';
import { Data, get, post } from '../../networking';
import _ from 'lodash';
import LocalStorage from '../../lib/local-storage';
import { ampLogEvent } from '../../lib/amplitude';
import { useCookies } from 'react-cookie';
import analytics from '../../lib/analytics';
import { FeaturedChallenge } from '../../components/challenge/types/challenge-dashboard.dto';
import { Links } from '../../components/links/index.interface';

async function postAuthLoginHash(payload: Data): Promise<Data> {
    return await post('auth/login/hash', payload);
}

const getFeaturedChallenge = async (context?: GetServerSidePropsContext): Promise<FeaturedChallenge> => {
    return get('challenge-dashboard/challenge/featured/', {}, context);
};

const getLifestyleChallenge = async (context?: GetServerSidePropsContext): Promise<FeaturedChallenge> => {
    return get(`challenge-dashboard/challenge/lifestyle/${context?.query?.slug[1]}`, {}, context);
};

export const getServerSideProps: GetServerSideProps = async (context: GetServerSidePropsContext) => {
    const redirectPath = context.query.slug[0];
    /**
     * Used to return metaData prop to main component
     */
    switch (redirectPath) {
    case Links.PROG:
        return {
            props: {
                metaData: {
                    ogTitle: 'See your progress with Sunnyside',
                    ogImage: '/img/progress-2-og-image.jpg',
                    ogUrl: 'https://app.sunnyside.co/l/prog',
                }
            }
        };
    case Links.INVITE_3:
        return {
            props: {
                metaData: {
                    ogTitle: 'Help grow the mindful drinking movement with Sunnyside',
                    ogImage: '/img/invite/referral-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/invite',
                }
            }
        };
    case Links.PLAN: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Create your weekly plan',
                    ogImage: '/img/dashboard/plan-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/plan'
                }
            }
        };
    }
    case Links.TARGETS: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Update your weekly targets',
                    ogImage: '/img/dashboard/targets-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/targets'
                }
            }
        };
    }
    case Links.COMMUNITY: {
        return {
            props: {
                metaData: {
                    ogTitle: 'See and respond to today\'s community reflection prompt',
                    ogImage: '/img/dashboard/community-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/community'
                }
            }
        };
    }
    case Links.JOURNAL: {
        return {
            props: {
                metaData: {
                    ogTitle: 'See and respond to today\'s community reflection prompt',
                    ogImage: '/img/dashboard/community-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/journal'
                }
            }
        };
    }
    case Links.POST: {
        return {
            props: {
                metaData: {
                    ogTitle: 'See and respond to today\'s community reflection prompt',
                    ogImage: '/img/dashboard/community-og-image.png',
                    ogUrl: `https://app.sunnyside.co/l/post/${context.query.slug[1]}`
                }
            }
        };
    }
    case Links.DRY_JANUARY: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Dry(ish) January Your Way | January 2025 Alcohol Challenge | Sunnyside',
                    ogImage: 'https://images.prismic.io/sunnysidetest/Z0S1Y68jQArT1Sfy_DryishJan2025-OGImage.png?auto=format,compress',
                    ogUrl: 'https://www.sunnyside.co/l/dry-january',
                    disableNoIndex: true
                }
            }
        };
    }
    case Links.DRY_JULY: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Dry(ish) July Your Way | July 2024 Alcohol Challenge | Sunnyside',
                    ogImage: '/img/challenge/dryish-july.png',
                    ogUrl: 'https://www.sunnyside.co/l/dry-july',
                    disableNoIndex: true
                }
            }
        };
    }
    case Links.CHALLENGE: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Reach your Sunnyside goals with a mindful drinking challenge.',
                    ogImage: '/img/challenge/challenge-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/challenge',
                }
            }
        };
    }
    case Links.CHALLENGE_COMPLETE: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Challenge completed! Tap to check out the progress you made.',
                    ogImage: '/img/challenge/challenge-og-image.png',
                    ogUrl: `https://app.sunnyside.co/l/challenge-complete/${context.query.slug[1]}`,
                }
            }
        };
    }
    case Links.CHALLENGE_INVITE: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Add friends to the challenge for an added layer of fun!',
                    ogImage: '/img/challenge/challenge-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/challenge-invite',
                }
            }
        };
    }
    case Links.CHALLENGE_FEATURED: {
        const featuredChallenge = await getFeaturedChallenge(context);
        return {
            props: {
                metaData: {
                    ogTitle: featuredChallenge?.ogTitle || 'Sunnyside\'s Featured Challenge',
                    ogImage: featuredChallenge?.ogImage || '/img/challenge/challenge-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/challenge-featured/'
                }
            }
        };
    }
    case Links.CHALLENGE_LIFESTYLE: {
        const lifestyleChallenge = await getLifestyleChallenge(context);
        return {
            props: {
                metaData: {
                    ogTitle: lifestyleChallenge?.ogTitle || 'Sunnyside\'s Lifestyle Challenge',
                    ogImage: lifestyleChallenge?.ogImage || '/img/challenge/challenge-og-image.png',
                    ogUrl: 'https://app.sunnyside.co/l/challenge-lifestyle/'
                }
            }
        };
    }
    case Links.CHALLENGE_NEW: {
        return {
            props: {
                metaData: {
                    ogTitle: 'Sunnyside - New Member Challenge',
                    ogImage: '/img/challenge/new-user-challenge.png',
                    ogUrl: 'https://app.sunnyside.co/l/challenge-new/',
                    ogDescription: 'Kickstart your Sunnyside experience with a new member challenge. Cut drinks 30% over the next 30 days!'
                }
            }
        };
    }

    default:
        return {
            props: {}
        };
    }
};

export default function ShortLinkRedirect(): JSX.Element {
    const router = useRouter();
    const [error] = useState('');
    const [cookies] = useCookies();
    const session = useContext(SessionContext);
    let amplitudeEvent = null;

    const { slug } = router.query;

    /**
     * Removes slug and returns only url params to pass onto redirect
     */
    let query = _.omit(router.query, 'slug');

    const redirectPath = slug[0];
    let userHash = slug[1];

    let pathname;

    switch (redirectPath) {
    /**
     * Choose Plan
     * Direct link to choose plan with 15 day trial
     * app.sunnyside.co/l/cp/<hash_id>
     */
    case Links.CP: {
        pathname = '/subscription_old/plans';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Choose Plan for $99 price plan with 20% discount off first year
     * Direct link to choose plan with 15 day trial
     * app.sunnyside.co/l/cp_20/<hash_id>
     */
    case Links.CP_20: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Extend Trial
     * Choose plan to extend trial URL (extend trial language at top, selecting plan starts 15 day trial)
     * app.sunnyside.co/l/et/<hash_id>
     */
    case Links.ET: {
        pathname = '/subscription_old/plans';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Immediate Upgrade
     * Direct link to upgrade page (choosing plan charges immediately, no trial):
     * app.sunnyside.co/l/iu/<hash_id>
     */
    case Links.IU: {
        pathname = '/subscription/plans';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Immediate Upgrade with Cyber Monday 20% Discount
     * Direct link to annual plan upgrade page (choosing plan charges immediately):
     * app.sunnyside.co/l/cm/<hash_id>
     */
    case Links.CM: {
        pathname = '/subscription_old/plans';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Immediate Upgrade with 2022 22% Discount
     * Direct link to annual plan upgrade page (choosing plan charges immediately):
     * app.sunnyside.co/l/2022/<hash_id>
     */
    case Links.TWENTY_TWENTY_TWO: {
        pathname = '/subscription_old/plans';
        query = {
            ...query,
            checkoutType: 'iu'
        };
        break;
    }
    /**
     * Switch Trial
     * Direct link to plan switch page, for current trial user (continues trial but on new plan)
     * app.sunnyside.co/l/st/<hash_id>
     */
    case Links.ST: {
        pathname = '/subscription_old/plans';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Switch Plan
     * Direct link to plan switch page, for active sub (switches plan and charges user for the difference OR issues credit if downgrading)
     * app.sunnyside.co/l/sp/<hash_id>
     */
    case Links.SP: {
        pathname = '/subscription_old/plans';
        query = { checkoutType: redirectPath };
        break;
    }
    /**
     * Switch Plan
     * Display only annual plan
     * Direct link to plan switch page, for active sub (switches plan and charges user for the difference OR issues credit if downgrading)
     * app.sunnyside.co/l/sp/<hash_id>
     */
    case Links.SP99_Y: {
        pathname = '/subscription_old/plans/1';
        query = { checkoutType: redirectPath };
        break;
    }
    /**
     * Switch Plan
     * 20% off first year for annual only
     * Display only annual plan
     * Direct link to plan switch page, for active sub (switches plan and charges user for the difference OR issues credit if downgrading)
     * app.sunnyside.co/l/sp/<hash_id>
     */
    case Links.SP99_Y_20: {
        pathname = '/subscription_old/plans/1';
        query = { checkoutType: redirectPath };
        break;
    }
    /**
     * Phone to Credit Card (trial) flow -- Growth Test
     * Direct link to choose plan with 15 day trial for user who have flowType = ptcc
     * app.sunnyside.co/l/ptcc/<hash_id>
     */
    case Links.PTCC: {
        pathname = '/subscription_old/plans';
        query = { checkoutType: redirectPath };
        break;
    }
    /**
     * Redirect user to progress dashboard 
     * app.sunnyside.co/l/prog/<hash_id>
     */
    case Links.PROG: {
        pathname = '/dashboard/progress';
        break;
    }
    /**
     * Redirect user to profile page
     * app.sunnyside.co/l/prof/<hash_id>
     */
    case Links.PROF: {
        pathname = '/dashboard';
        break;
    }
    /**
     * Redirect user to invite page
     * app.sunnyside.co/l/invite/<hash_id>
     */
    case Links.INVITE: {
        pathname = '/invite';
        break;
    }
    /**
     * Redirect user to incentivized referral invite page
     * app.sunnyside.co/l/invite/<hash_id>
     */
    case Links.INVITE_3: {
        pathname = '/invite/3';
        break;
    }
    /**
     * Redirect user to tracking page
     * app.sunnyside.co/l/track/<hash_id>
     */
    case Links.TRACK: {
        pathname = '/dashboard/tracking';
        break;
    }
    /**
     * Redirect user to tracking page
     * The web app currently does not support notes, so we don't need to open up the TrackingDayModal
     * app.sunnyside.co/l/track-day/<day_id>/<hash_id>
     */
    case Links.TRACK_DAY: {
        pathname = '/dashboard/tracking';
        userHash = slug[2];
        break;
    }
    /**
     * Redirect user to targets modal for current week
     * app.sunnyside.co/l/tracking/<hash_id>
     */
    case Links.TARGETS: {
        pathname = '/dashboard/tracking';
        query = {
            ...query,
            targets: '1'
        };
        break;
    }
    /**
     * Redirect user to confirm modal for yesterday
     * app.sunnyside.co/l/confirm/<hash_id>
     */
    case Links.CONFIRM: {
        pathname = '/dashboard/tracking';
        query = {
            ...query,
            confirm: '1'
        };
        break;
    }
    /**
     * Redirect user to afternoon reminder modal
     * app.sunnyside.co/l/afternoon-reminder/<hash_id>
     */
    case Links.AFTERNOON_REMINDER: {
        pathname = '/dashboard/tracking';
        query = {
            ...query,
            afternoonReminder: '1'
        };
        break;
    }
    /**
     * Redirect user to plan creation
     * app.sunnyside.co/l/plan/<hash_id>
     */
    case Links.PLAN: {
        pathname = '/dashboard/plan';
        break;
    }
    /**
     * Redirect user to plan confirm for upcoming/current/or specific week based on params
     * app.sunnyside.co/l/see-plan/<hash_id>
     */
    case Links.SEE_PLAN: {
        pathname = '/dashboard/plan/confirm';
        break;
    }
    /**
     * Redirect user to diagnostic
     * app.sunnyside.co/l/diag/<hash_id>
     */
    case Links.DIAG: {
        pathname = '/diagnostic';
        break;
    }
    /**
     * Redirect user to diagnostic
     * app.sunnyside.co/l/diag/<hash_id>
     */
    case Links.DIAG_ID: {
        pathname = `/diagnostic/${slug[1]}`;
        userHash = slug[2];
        break;
    }
    /**
     * Choose Plan for $99 price plan
     * Direct link to choose plan with 15 day trial
     * app.sunnyside.co/l/cp99/<hash_id>
     */
    case Links.CP99: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: 'cp'
        };
        break;
    }
    /**
     * Choose Plan for $99 price plan with 20% off
     * Direct link to annual plan only with 15 day trial
     * app.sunnyside.co/l/cp99/<hash_id>
     */
    case Links.CP99_20: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Immediate Upgrade for $99 plan
     * Direct link to upgrade page (choosing plan charges immediately, no trial):
     * app.sunnyside.co/l/iu99/<hash_id>
     */
    case Links.IU99: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: 'iu'
        };
        break;
    }
    /**
     * Immediate Upgrade with 20% Discount for $99 plan
     * Show only annual plan
     * Direct link to annual plan upgrade page (choosing plan charges immediately):
     * app.sunnyside.co/l/iu99_20/<hash_id>
     */
    case Links.IU99_20: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Immediate Upgrade with 20% Discount for $99 plan
     * Show all plans
     * Direct link to annual plan upgrade page (choosing plan charges immediately):
     * app.sunnyside.co/l/iu99_20_all/<hash_id>
     */
    case Links.IU99_20_ALL: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    /**
     * Immediate Upgrade to $99 plan
     * Show annual plan only
     * Direct link to annual plan upgrade page (choosing plan charges immediately):
     * app.sunnyside.co/l/iu99_y/<hash_id>
     */
    case Links.IU99_Y: {
        pathname = '/subscription_old/plans/1';
        query = {
            ...query,
            checkoutType: redirectPath
        };
        break;
    }
    case Links.NOTIFICATION_CONFIG: {
        pathname = '/dashboard/notifications/preferences';
        break;
    }
    /**
     * Redirect user to progress dashboard 
     * app.sunnyside.co/l/prog/<hash_id>
     */
    case Links.CONFIG: {
        pathname = '/dashboard/coaching-config';
        break;
    }
    /**
     * One click reactivation
     */
    case Links.REACTIVATE: {
        pathname = '/subscription/reactivate';
        break;
    }
    /**
     * One click reactivation
     */
    case Links.ADD_PAYMENT: {
        pathname = '/subscription/add-payment';
        break;
    }
    /**
     * Redirect user to community landing page
     * app.sunnyside.co/l/community/<hash_id>
     */
    case Links.COMMUNITY: {
        pathname = '/dashboard/community';
        break;
    }
    /**
     * Redirect user to journal landing page
     * app.sunnyside.co/l/journal/<hash_id>
     */
    case Links.JOURNAL: {
        pathname = 'dashboard/community';
        break;
    }
    /**
     * Redirect user to community post landing page
     * app.sunnyside.co/l/post/<post_id>/<hash_id>
     */
    case Links.POST: {
        pathname = 'dashboard/community';
        break;
    }
    /**
     * Redirect dry january config - for active users
     * app.sunnyside.co/l/dryish-january/<hash_id>
     */
    case Links.DRY_JANUARY: {
        pathname = '/challenge/opt-in/dryish-january-2025';
        break;
    }
    /**
     * Redirect dry july config - for active users
     * app.sunnyside.co/l/dryish-july/<hash_id>
     */
    case Links.DRY_JULY: {
        pathname = '/challenge/opt-in/dryish-july-2024';
        break;
    }
    /**
     * Redirect soberish mom config - for active users
     * app.sunnyside.co/l/soberish-mom/<hash_id>
     */
    case Links.SOBERISH_MOM: {
        pathname = '/challenge/opt-in/soberishmom';
        break;
    }
    /**
     * Redirect dry january config - for reactivations
     * app.sunnyside.co/l/dryish-january-reactivate/<hash_id>
     * OPTIONAL: ?checkoutType=cp99_20 will determine the checkout for the user
     */
    case Links.DRY_JANUARY_REACTIVATE: {
        LocalStorage.set('challenge-reactivation', 'true');
        LocalStorage.set('checkout-success-url', '/challenge/invite?userType=churned_user_winback');
        pathname = '/challenge/opt-in/dryish-january-2025';
        query = {
            ...query,
            checkout: 'iu'
        };
        break;
    }
    /**
     * Redirect dry july config - for reactivations
     * app.sunnyside.co/l/dryish-july-reactivate/<hash_id>
     * OPTIONAL: ?checkoutType=cp99_20 will determine the checkout for the user
     */
    case Links.DRY_JULY_REACTIVATE: {
        LocalStorage.set('challenge-reactivation', 'true');
        LocalStorage.set('checkout-success-url', '/challenge/invite?userType=churned_user_winback');
        pathname = '/challenge/opt-in/dryish-july-2024';
        query = {
            ...query,
            checkout: 'iu99_y'
        };
        break;
    }
    /**
     * Redirect user to challenge dashboard
     * app.sunnyside.co/l/challenge/<hash_id>
     */
    case Links.CHALLENGE: {
        pathname = '/dashboard/challenge';
        query = { tab: 'active' };
        break;
    }

    /**
     * Redirect user to challenge completed tab
     * app.sunnyside.co/l/challenge/<challenge_identifier>/<hash_id>
     */
    case Links.CHALLENGE_COMPLETE: {
        pathname = '/dashboard/challenge';
        query = { tab: 'completed' };
        userHash = slug[2];
        break;
    }
    /**
     * Redirect user to challenge opt in page with reactivation
     * app.sunnyside.co/l/challenge/<challenge_identifier>/<hash_id>
     * OPTIONAL: ?checkoutType=cp99_20 will determine the checkout for the user
     */
    case Links.CHALLENGE_REACTIVATE: {
        LocalStorage.set('challenge-reactivation', 'true');
        LocalStorage.set('checkout-success-url', '/challenge/invite?userType=churned_user_winback');
        pathname = `/challenge/opt-in/${slug[1]}`;
        userHash = slug[2];
        break;
    }
    /**
     * Redirect user to challenge invite page
     * app.sunnyside.co/l/challenge-invite
     */
    case Links.CHALLENGE_INVITE: {
        pathname = '/dashboard/challenge/invite';
        break;
    }
    /**
     * Redirect user to challenge featured page
     * app.sunnyside.co/l/challenge-featured
     */
    case Links.CHALLENGE_FEATURED: {
        pathname = '/dashboard/challenge/featured';
        break;
    }
    /**
     * Redirect user to challenge featured page
     * app.sunnyside.co/l/challenge-lifestyle
     */
    case Links.CHALLENGE_LIFESTYLE: {
        if (slug[1]) {
            pathname = '/dashboard/challenge/featured';
            query = {
                identifier: slug[1]
            };
            userHash = slug[2];
        }
        break;
    }
    case Links.CHALLENGE_NEW: {
        pathname = '/dashboard/challenge/opt-in/new_user_taper';
        query = {
            preference: 'generic_taper'
        };
        break;
    }
    /**
     * Upscale to Premium Coaching
     * app.sunnyside.co/l/prem298ann/<hash_id>
     */
    case Links.PREM298ANN: {
        pathname = '/subscription/plans';
        query = {
            ...query,
            ct: 'st',
            pg: 'prem_298',
            pv: 'annual_only',
            tr: 'PREMIUM_COACHING'
        };
        break;
    }
    /**
     * Upscale to Premium Coaching with 20% off
     * app.sunnyside.co/l/prem298ann/<hash_id>
     */
    case Links.PREM298ANN20: {
        pathname = '/subscription/plans';
        query = {
            ...query,
            ct: 'st',
            pg: 'prem_298',
            pv: 'annual_only',
            c: 'sp_20',
            tr: 'PREMIUM_COACHING'
        };
        break;
    }
    /**
     * Redirect dry january config - for active users
     * app.sunnyside.co/l/february-challenge/<hash_id>
     */
    case Links.FEABRUARY_CHALLENGE: {
        pathname = '/february-challenge';
        break;
    }
    /**
     * Redirect user to coaching
     * app.sunnyside.co/l/coaching/<hash_id>
     */
    case Links.COACHING:
    case Links.RESOURCES:
    case Links.RESOURCES_READING:
    case Links.RESOURCES_EXERCISE:
    case Links.RESOURCES_VIDEO: {
        pathname = '/dashboard/coaching';
        break;
    }

    /**
     * Redirect user to typeform
     * app.sunnyside.co/l/fwd/<hash_id>
     */
    case Links.FWD: {
        const typeformId = query.tfId;
        pathname = `https://forms.sunnyside.co/to/${typeformId}`;
        query = _.omit(query, 'tfId');

        amplitudeEvent = {
            name: 'mat: landed on hand off flow',
            data: { typeformId }
        };
        break;
    }

    /**
     * Redirect user to community to download the app
     * app.sunnyside.co/l/community/<hash_id>
     */
    case Links.APP_REVIEW:{
        pathname = '/dashboard/community';
        break;
    }

    /**
     * Redirect user to community to download the app
     * app.sunnyside.co/l/community/<hash_id>
     */
    case Links.APPLE_HEALTH:{
        pathname = '/dashboard/community';
        break;
    }

    case Links.ONBOARDING: {
        pathname = '/onboarding';
        break;
    }

    }

    useEffect(() => {
        if (session.initialized) {
            if (!pathname) {
                router.push('https://sunnyside.go.link/9TMma');
                return;
            }
            if (userHash) {
                postAuthLoginHash({
                    hash: userHash
                }).then(response => {
                    if (response.id) {
                        refreshUserAndStoreIn(session).then(userData => {
                            if (!userData.message) {
                                session.setUser(userData);
                                router.replace({ pathname, query });
                            } else {
                                router.replace({ pathname, query });
                            }
                        }).catch(() => {
                            router.replace({ pathname, query });
                        });
                    } else {
                        router.replace({ pathname, query });
                    }
                }).catch(() => {
                    router.replace({ pathname, query });
                }).finally(() => {
                    if (amplitudeEvent) {
                        if (query.u) {
                            analytics.identify(query.u as string);
                            ampLogEvent(
                                amplitudeEvent.name,
                                amplitudeEvent.data,
                                cookies
                            );
                        }
                        amplitudeEvent = null;
                    }
                });
            } else {
                router.replace({ pathname, query });
            }
        }
    }, [session.initialized]);

    return (
        <Layout>
            <Head>
                <title>Redirecting...</title>
            </Head>
            <Box>
                {!error && <Loading />}
                <Typography>{error ? error : 'Redirecting...'}</Typography>
            </Box>
        </Layout>
    );
}
